import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SupportMessage, SupportTicketStatus } from '@/types';
import {
  AdminTicket,
  SupportMessageRow,
  fetchTicketMessagesAdmin,
  fetchTicketsAdmin,
  mapSupportMessageRow,
  markThreadReadByAdmin,
  sendAdminReply,
  setTicketStatus,
} from '@/services/supportService';
import { Button } from '@/components/ui/button';

const STATUS_LABEL: Record<SupportTicketStatus, string> = {
  open: 'Offen',
  answered: 'Beantwortet',
  closed: 'Geschlossen',
};

const STATUS_STYLE: Record<SupportTicketStatus, string> = {
  open: 'bg-amber-500/10 text-amber-600',
  answered: 'bg-sky-500/10 text-sky-600',
  closed: 'bg-muted text-muted-foreground',
};

type StatusFilter = 'all' | SupportTicketStatus;

export const AdminSupportView: React.FC = () => {
  const [tickets, setTickets] = useState<AdminTicket[]>([]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openSeqRef = useRef(0);

  const loadTickets = useCallback(async () => {
    setIsLoadingList(true);
    try {
      setTickets(await fetchTicketsAdmin());
    } catch {
      // im Service geloggt
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => { void loadTickets(); }, [loadTickets]);

  // Refs für den Realtime-Handler: der Channel wird einmal aufgebaut,
  // muss aber immer den aktuellen Selektions-/Listen-Stand sehen.
  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  const ticketsRef = useRef<AdminTicket[]>([]);
  ticketsRef.current = tickets;

  // Realtime: neue Nachrichten landen live in Liste + offenem Thread (Publication: Migration 122).
  // Eigene Admin-Antworten sind optimistisch im State (ID-Dedupe fängt das Echo ab);
  // Antworten des jeweils anderen Admins erscheinen so ebenfalls live.
  useEffect(() => {
    const channel = supabase
      .channel('admin-support-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages' },
        (payload) => {
          const row = payload.new as SupportMessageRow;
          const isOpenThread = selectedIdRef.current === row.ticket_id;

          if (isOpenThread) {
            setMessages((prev) =>
              prev.some((m) => m.id === row.id) ? prev : [...prev, mapSupportMessageRow(row)],
            );
            // Thread ist offen → direkt als gelesen markieren
            if (row.sender_role === 'user') void markThreadReadByAdmin(row.ticket_id);
          }

          if (row.sender_role !== 'user') return;

          if (!ticketsRef.current.some((t) => t.id === row.ticket_id)) {
            // Erste Nachricht eines neuen Tickets → Liste inkl. Profil-Join nachladen
            void loadTickets();
            return;
          }
          setTickets((prev) =>
            prev
              .map((t) =>
                t.id === row.ticket_id
                  ? { ...t, updatedAt: row.created_at, unreadCount: isOpenThread ? 0 : t.unreadCount + 1 }
                  : t,
              )
              .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
          );
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [loadTickets]);

  const openTicket = useCallback(async (id: string) => {
    // Identity-Guard: nur der zuletzt geöffnete Thread darf nach einem await noch Thread-State schreiben
    const seq = ++openSeqRef.current;
    setSelectedId(id);
    setIsLoadingThread(true);
    setError(null);
    try {
      const loaded = await fetchTicketMessagesAdmin(id);
      if (seq === openSeqRef.current) setMessages(loaded);
      await markThreadReadByAdmin(id);
      // bewusst ungeguarded: der Server HAT gelesen markiert — Badge-Nullung bleibt auch für ein nicht mehr selektiertes Ticket korrekt
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, unreadCount: 0 } : t)));
    } catch {
      if (seq === openSeqRef.current) setError('Thread konnte nicht geladen werden.');
    } finally {
      if (seq === openSeqRef.current) setIsLoadingThread(false);
    }
  }, []);

  const handleReply = useCallback(async () => {
    const body = draft.trim();
    if (!body || !selectedId || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const message = await sendAdminReply(selectedId, body);
      setDraft('');
      setMessages((prev) => [...prev, message]);
      setTickets((prev) => prev.map((t) => (t.id === selectedId ? { ...t, status: 'answered' } : t)));
    } catch {
      setError('Senden fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setIsSending(false);
    }
  }, [draft, selectedId, isSending]);

  const handleClose = useCallback(async () => {
    if (!selectedId) return;
    try {
      await setTicketStatus(selectedId, 'closed');
      setTickets((prev) => prev.map((t) => (t.id === selectedId ? { ...t, status: 'closed' } : t)));
    } catch {
      setError('Status konnte nicht geändert werden.');
    }
  }, [selectedId]);

  const visible = tickets.filter((t) => filter === 'all' || t.status === filter);
  const selected = tickets.find((t) => t.id === selectedId) ?? null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
      {/* Ticketliste */}
      <div>
        <div className="flex gap-1 mb-3">
          {(['all', 'open', 'answered', 'closed'] as StatusFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                filter === f ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {f === 'all' ? 'Alle' : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
        {isLoadingList ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Keine Anfragen.</p>
        ) : (
          <div className="space-y-1.5 max-h-[32rem] overflow-y-auto pr-1">
            {visible.map((t) => (
              <button
                key={t.id}
                onClick={() => void openTicket(t.id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  selectedId === t.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{t.userName || t.userEmail || 'Unbekannt'}</p>
                  {t.unreadCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
                      {t.unreadCount}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(t.updatedAt).toLocaleDateString('de-DE')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Thread */}
      <div className="rounded-lg border border-border bg-card p-4 flex flex-col min-h-[24rem]">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Anfrage auswählen
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border pb-3 mb-3">
              <div>
                <p className="text-sm font-semibold">{selected.userName || 'Unbekannt'}</p>
                <p className="text-xs text-muted-foreground">{selected.userEmail}</p>
              </div>
              {selected.status !== 'closed' && (
                <Button variant="outline" size="sm" onClick={() => void handleClose()}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Schließen
                </Button>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
              {isLoadingThread ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.senderRole === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.senderRole === 'admin' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'
                    }`}>
                      {m.body}
                      <div className={`text-[10px] mt-1 ${m.senderRole === 'admin' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {new Date(m.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {error && <p className="text-xs text-destructive mt-2">{error}</p>}

            <div className="mt-3 flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleReply(); }
                }}
                placeholder="Antwort schreiben…"
                rows={2}
                className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <Button size="sm" onClick={() => void handleReply()} disabled={!draft.trim() || isSending} aria-label="Antworten">
                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
